/*
 * AutomationEngineCard
 * ---------------------
 * The premium visual surface for the Autopilot AUTOMATION ENGINE.
 *
 * Owner intent this component satisfies (Feb 2026, P0 Autopilot UI Overhaul):
 *   1. Make the automation state instantly readable at a glance.
 *   2. Show queue coverage over the 10-day horizon as a visual bar.
 *   3. Surface the NEXT scheduled post (date/time · platform · caption
 *      preview · optional thumbnail) so the owner sees what Autopilot
 *      just generated on their behalf.
 *   4. Show `last_refill_*` diagnostics in friendly copy — never raw
 *      enum strings.
 *   5. Surface a queue-health chip (HEALTHY · FILLING · QUOTA LIMITED ·
 *      PAUSED · ERROR) with friendly copy + optional action.
 *   6. Deduplicate the "Autopilot filled N posts" toast so a 15s poll
 *      loop never spams the owner.
 *   7. Poll `/api/autopilot/status` every 15s while mounted; also
 *      refresh immediately after Pause / Resume / manual actions the
 *      parent triggers (via `onExternalRefresh` prop).
 *
 * All state is derived from `/api/autopilot/status` — NO invented UI
 * state, NO fabricated progress, NO engine mutation. Pause / Resume
 * post to existing endpoints only.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PauseCircle,
  PlayCircle,
  Loader2,
  Zap,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Clock4,
  Rocket,
  Calendar,
  Info,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GlassCard from "@/components/GlassCard";
import api from "@/lib/api";

const POLL_MS = 15000;

// ---------------------------------------------------------------
// Small utilities — kept local so this component is drop-in.
// ---------------------------------------------------------------
function _fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return "—";
  }
}

function _platformIconChar(p) {
  const s = String(p || "").toLowerCase();
  if (s.startsWith("insta")) return "IG";
  if (s.startsWith("face")) return "FB";
  if (s.startsWith("linked")) return "LI";
  if (s === "x" || s.startsWith("twit")) return "X";
  return String(p || "?").slice(0, 2).toUpperCase();
}

// Map backend status flags → friendly UI state.
// Order matters: PAUSED > ERROR > QUOTA_LIMITED > FILLING > HEALTHY.
function _healthFromStatus(s) {
  if (!s) return "healthy";
  if (s.paused) return "paused";
  if (s.last_refill_reason === "failure_cap_hit") return "error";
  if (s.quota_blocked) return "quota_limited";
  if ((s.missing_slots || 0) > 0) return "filling";
  return "healthy";
}

// Friendly copy for the refill status. Owner-facing — NEVER raw enum.
function _refillCopy(s) {
  if (!s) return { title: "Autopilot is warming up…", tone: "muted" };
  const gen = s.last_refill_generated_count || 0;
  const skipped = s.last_refill_skipped_count || 0;
  const failed = s.last_refill_failed_count || 0;
  const reason = s.last_refill_reason;
  const horizon = s.lookahead_horizon_days || 10;
  const missing = s.missing_slots || 0;
  if (!s.last_refill_at && !gen) {
    return {
      title: s.paused
        ? "Autopilot is paused"
        : "Autopilot has not filled a queue yet",
      body: s.paused
        ? "Resume to have Postmoro fill the next 10 days."
        : "Turn on Autopilot to have Postmoro fill your next 10 days automatically.",
      tone: "muted",
    };
  }
  if (reason === "horizon_covered" || (missing === 0 && gen === 0)) {
    return {
      title: "Your queue is fully covered",
      body: `Postmoro will keep checking every few hours and refill as posts move through the next ${horizon} days.`,
      tone: "success",
    };
  }
  if (s.quota_blocked || reason === "quota_blocked") {
    return {
      title: gen > 0
        ? `Filled ${gen} post${gen === 1 ? "" : "s"} — ${missing} more waiting on your AI limit`
        : "Waiting on your monthly AI limit",
      body:
        "Autopilot has generated as much as your current plan allows. It'll resume automatically at your next renewal, or upgrade to keep filling ahead.",
      tone: "warn",
    };
  }
  if (reason === "failure_cap_hit") {
    return {
      title: "Autopilot had trouble filling part of your schedule",
      body: `${gen} post${gen === 1 ? "" : "s"} landed, but Postmoro paused refill after a few failed attempts. Retry Resume to try again.`,
      tone: "danger",
    };
  }
  if (reason === "paused_mid_run") {
    return {
      title: "Autopilot was paused mid-refill",
      body: `${gen} post${gen === 1 ? "" : "s"} were filled before you paused. Resume to continue.`,
      tone: "muted",
    };
  }
  if (reason === "cfg_incomplete") {
    return {
      title: "Autopilot needs a schedule",
      body: "Add posting days, times, and at least one platform in your Autopilot settings to start filling.",
      tone: "warn",
    };
  }
  if (reason === "not_active") {
    return {
      title: "Autopilot is not active",
      body: "Enable Autopilot in settings to start filling your queue.",
      tone: "muted",
    };
  }
  // Default healthy-fill message.
  if (gen > 0) {
    return {
      title: `Filled ${gen} post${gen === 1 ? "" : "s"} for the next ${horizon} days`,
      body: skipped > 0
        ? `${skipped} slot${skipped === 1 ? "" : "s"} skipped (already covered).`
        : "Everything on schedule.",
      tone: "success",
    };
  }
  return {
    title: "Autopilot is running",
    body: "Postmoro will keep your queue filled automatically.",
    tone: "muted",
  };
}

// Health chip metadata.
function _healthChip(state) {
  switch (state) {
    case "healthy":
      return {
        label: "Healthy",
        Icon: CheckCircle2,
        cls: "text-[color:var(--brand-primary)] border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.10)]",
        body: "Every configured slot in the next 10 days is covered.",
      };
    case "filling":
      return {
        label: "Filling",
        Icon: Loader2,
        cls: "text-[color:var(--accent-cool)] border-[color:rgba(47,143,229,0.55)] bg-[color:rgba(47,143,229,0.10)]",
        body: "Postmoro is filling missing slots in your queue.",
        spin: true,
      };
    case "quota_limited":
      return {
        label: "Quota Limited",
        Icon: AlertTriangle,
        cls: "text-[color:var(--accent-warm)] border-[color:rgba(255,190,110,0.55)] bg-[color:rgba(255,190,110,0.10)]",
        body: "Autopilot hit your monthly AI generation limit.",
      };
    case "paused":
      return {
        label: "Paused",
        Icon: PauseCircle,
        cls: "text-[color:var(--accent-warm)] border-[color:rgba(255,190,110,0.55)] bg-[color:rgba(255,190,110,0.10)]",
        body: "Autopilot is not creating new content.",
      };
    case "error":
      return {
        label: "Needs Attention",
        Icon: AlertTriangle,
        cls: "text-[color:var(--danger)] border-[color:rgba(255,92,108,0.55)] bg-[color:rgba(255,92,108,0.10)]",
        body: "Autopilot had trouble filling part of your schedule.",
      };
    default:
      return {
        label: "Idle",
        Icon: Clock4,
        cls: "text-[color:var(--text-3)] border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]",
        body: "Autopilot is idle.",
      };
  }
}

// ---------------------------------------------------------------
// Sub-cards (kept small — <50 lines each per coding guidelines).
// ---------------------------------------------------------------
function CoverageBar({ status }) {
  const total = status?.required_slots_in_horizon || 0;
  const covered = status?.covered_slots || 0;
  const pct = total > 0 ? Math.min(100, Math.round((covered / total) * 100)) : 0;
  const days = status?.lookahead_horizon_days || 10;
  const missing = status?.missing_slots || 0;
  return (
    <div
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4"
      data-testid="autopilot-coverage-card"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
          Queue coverage
        </div>
        <span
          data-testid="autopilot-coverage-count"
          className="text-xs text-[color:var(--text-2)]"
        >
          {covered} of {total} slots filled
        </span>
      </div>
      <div className="mt-2 h-2 w-full rounded-full bg-[color:var(--surface-3)] overflow-hidden">
        <div
          data-testid="autopilot-coverage-bar-fill"
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: total === covered && total > 0
              ? "linear-gradient(90deg, rgba(27,168,159,0.9), rgba(27,168,159,0.6))"
              : "linear-gradient(90deg, rgba(47,143,229,0.9), rgba(47,143,229,0.55))",
          }}
        />
      </div>
      <div className="mt-2 text-xs text-[color:var(--text-3)]" data-testid="autopilot-coverage-label">
        {total === 0
          ? "Add posting days, times and a platform to start filling."
          : missing === 0
          ? `Next ${days} days covered.`
          : `${missing} post${missing === 1 ? "" : "s"} still being filled in the next ${days} days.`}
      </div>
    </div>
  );
}

function NextPostCard({ status }) {
  const np = status?.next_post;
  if (!np) {
    return (
      <div
        className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4"
        data-testid="autopilot-next-post-empty"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
          Next post
        </div>
        <div className="mt-2 text-sm text-[color:var(--text-2)]">
          {status?.paused
            ? "Autopilot is paused. Resume to see what's next."
            : "No upcoming post yet. Postmoro will drop the next one here as soon as it's generated."}
        </div>
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4"
      data-testid="autopilot-next-post-card"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
          Next post
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:rgba(27,168,159,0.15)] text-[color:var(--brand-primary)]"
          data-testid="autopilot-next-post-status"
        >
          {np.status === "pending_approval" ? "Awaiting approval" : "Scheduled"}
        </span>
      </div>
      <div className="mt-2 flex items-start gap-3">
        <div
          className="h-10 w-10 rounded-md bg-[color:var(--surface-3)] text-[10px] uppercase font-semibold flex items-center justify-center text-[color:var(--text-2)] shrink-0"
          data-testid="autopilot-next-post-platform"
          aria-label={np.platform || "post"}
        >
          {_platformIconChar(np.platform)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[color:var(--text-1)]" data-testid="autopilot-next-post-when">
            {_fmtDateTime(np.scheduled_at)}
          </div>
          {np.caption ? (
            <div
              className="mt-1 text-xs text-[color:var(--text-3)] line-clamp-2"
              data-testid="autopilot-next-post-caption"
            >
              {String(np.caption).slice(0, 160)}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function RefillStatusBlock({ status }) {
  const copy = _refillCopy(status);
  const toneCls =
    copy.tone === "success"
      ? "text-[color:var(--brand-primary)]"
      : copy.tone === "warn"
      ? "text-[color:var(--accent-warm)]"
      : copy.tone === "danger"
      ? "text-[color:var(--danger)]"
      : "text-[color:var(--text-2)]";
  return (
    <div
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4"
      data-testid="autopilot-refill-status"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
        Last refill
      </div>
      <div className={"mt-2 text-sm font-medium " + toneCls} data-testid="autopilot-refill-title">
        {copy.title}
      </div>
      {copy.body ? (
        <div
          className="mt-1 text-xs text-[color:var(--text-3)] leading-relaxed"
          data-testid="autopilot-refill-body"
        >
          {copy.body}
        </div>
      ) : null}
      {status?.last_refill_at ? (
        <div className="mt-2 text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
          {_fmtDateTime(status.last_refill_at)}
        </div>
      ) : null}
    </div>
  );
}

function HealthChip({ state }) {
  const chip = _healthChip(state);
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider " +
        chip.cls
      }
      data-testid={`autopilot-health-chip-${state}`}
      title={chip.body}
    >
      <chip.Icon className={"h-3.5 w-3.5 " + (chip.spin ? "animate-spin" : "")} />
      {chip.label}
    </span>
  );
}

function ConfigSummary({ status, cfg }) {
  // `cfg` from /autopilot/settings is passed in by parent; falls back
  // to status fields when available. We deliberately DO NOT re-fetch —
  // keeps this component read-only against one source of truth.
  const days = (cfg?.posting_days || []).join(" · ");
  const times = (cfg?.posting_times || []).join(" · ");
  const platforms = (status?.platforms || cfg?.target_platforms || []).join(", ");
  const focus = (status?.content_focus || cfg?.content_focus || []).join(", ");
  const approvalMode = status?.auto_publish ? "Auto-publish" : "Require approval";
  return (
    <div
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4"
      data-testid="autopilot-config-summary"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
        Schedule
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Days</div>
          <div className="text-[color:var(--text-1)] font-medium" data-testid="autopilot-config-days">
            {days || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Times</div>
          <div className="text-[color:var(--text-1)] font-medium" data-testid="autopilot-config-times">
            {times || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Platforms</div>
          <div className="text-[color:var(--text-1)] font-medium" data-testid="autopilot-config-platforms">
            {platforms || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Approval</div>
          <div className="text-[color:var(--text-1)] font-medium" data-testid="autopilot-config-approval">
            {approvalMode}
          </div>
        </div>
        {focus ? (
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Focus</div>
            <div className="text-[color:var(--text-1)] font-medium" data-testid="autopilot-config-focus">
              {focus}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <details
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--text-2)]"
      data-testid="autopilot-how-it-works"
    >
      <summary className="cursor-pointer select-none flex items-center gap-2 font-medium text-[color:var(--text-1)]">
        <Info className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
        How Autopilot works
      </summary>
      <div className="mt-2 text-[color:var(--text-3)] leading-relaxed">
        Postmoro keeps your upcoming schedule filled automatically. As posts
        move through your calendar, Autopilot creates new content to maintain
        your future queue until you pause it. It checks every few hours and
        fills the next 10 days at a time — no manual clicks needed.
      </div>
    </details>
  );
}

// ---------------------------------------------------------------
// Main component
// ---------------------------------------------------------------
export default function AutomationEngineCard({
  cfg,
  onCfgRefresh,          // called after Pause/Resume so parent can re-load /autopilot/settings
  externalRefreshKey,    // parent increments this to force an immediate status refresh
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  // Toast dedupe: track the last refill_at we've already shown so a
  // 15s poll never re-toasts the same event.
  const lastToastedAt = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/autopilot/status");
      setStatus(r.data);
    } catch (_e) {
      // Silent — the parent already handles auth flow; we don't want a
      // per-poll toast spam.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (externalRefreshKey === undefined) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalRefreshKey]);

  // Refill toast dedupe.
  useEffect(() => {
    if (!status || !status.last_refill_at) return;
    if (lastToastedAt.current === status.last_refill_at) return;
    // Only toast on genuine "we did something worth telling you about":
    // generated > 0 OR reason=horizon_covered right after a refill.
    const gen = status.last_refill_generated_count || 0;
    const reason = status.last_refill_reason;
    if (gen > 0) {
      const horizon = status.lookahead_horizon_days || 10;
      toast.success(
        `Autopilot filled ${gen} post${gen === 1 ? "" : "s"} for the next ${horizon} days`,
        { id: "autopilot-refill-" + status.last_refill_at },
      );
      lastToastedAt.current = status.last_refill_at;
    } else if (reason === "horizon_covered" && !lastToastedAt.current) {
      toast.success("Your Autopilot queue is fully covered", {
        id: "autopilot-refill-" + status.last_refill_at,
      });
      lastToastedAt.current = status.last_refill_at;
    } else {
      lastToastedAt.current = status.last_refill_at;
    }
  }, [status]);

  const health = useMemo(() => _healthFromStatus(status), [status]);

  const onPause = async () => {
    setPausing(true);
    try {
      await api.post("/autopilot/pause");
      toast.success("Autopilot paused");
      await refresh();
      if (onCfgRefresh) await onCfgRefresh();
    } catch (_e) {
      toast.error("Could not pause Autopilot");
    } finally {
      setPausing(false);
    }
  };
  const onResume = async () => {
    setResuming(true);
    try {
      await api.post("/autopilot/resume");
      toast.success("Autopilot resumed — filling your queue now…");
      await refresh();
      if (onCfgRefresh) await onCfgRefresh();
    } catch (_e) {
      toast.error("Could not resume Autopilot");
    } finally {
      setResuming(false);
    }
  };

  const active = !!status?.active;
  const paused = !!status?.paused;
  const stateLabel = active ? "ACTIVE" : paused ? "PAUSED" : "OFF";
  const stateColor = active
    ? "text-[color:var(--brand-primary)]"
    : paused
    ? "text-[color:var(--accent-warm)]"
    : "text-[color:var(--text-3)]";
  const subtitle = active
    ? "Postmoro is automatically keeping your content calendar filled."
    : paused
    ? "Postmoro is not creating new Autopilot content."
    : "Turn on Autopilot in settings to start filling your queue automatically.";

  return (
    <GlassCard className="p-6 mb-5 relative overflow-hidden" data-testid="automation-engine-card">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-20 h-72 w-72 rounded-full"
        style={{
          background: active
            ? "radial-gradient(closest-side, rgba(27,168,159,0.35), transparent 70%)"
            : "radial-gradient(closest-side, rgba(255,190,110,0.20), transparent 70%)",
        }}
      />
      <div className="relative">
        {/* Header row */}
        <div className="flex items-start gap-4 flex-wrap">
          <div className="min-w-[260px] flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--brand-primary)] font-semibold flex items-center gap-2">
              <Zap className="h-3.5 w-3.5" />
              Automation Engine
            </div>
            <div
              className={"font-display text-3xl sm:text-4xl font-semibold mt-1 leading-tight " + stateColor}
              data-testid="automation-engine-state-label"
            >
              AUTOPILOT {stateLabel}
            </div>
            <div className="text-sm text-[color:var(--text-2)] mt-2 max-w-[540px]" data-testid="automation-engine-subtitle">
              {subtitle}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <HealthChip state={health} />
            {paused ? (
              <Button
                onClick={onResume}
                disabled={resuming}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold min-w-[140px]"
                data-testid="automation-engine-resume-btn"
              >
                {resuming ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="h-4 w-4 mr-2" />
                )}
                Resume Autopilot
              </Button>
            ) : active ? (
              <Button
                variant="outline"
                onClick={onPause}
                disabled={pausing}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] min-w-[140px]"
                data-testid="automation-engine-pause-btn"
              >
                {pausing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PauseCircle className="h-4 w-4 mr-2" />
                )}
                Pause Autopilot
              </Button>
            ) : null}
          </div>
        </div>

        {/* Grid of engine facts */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="automation-engine-grid">
          <CoverageBar status={status} />
          <NextPostCard status={status} />
          <RefillStatusBlock status={status} />
          <ConfigSummary status={status} cfg={cfg} />
        </div>

        {/* Next automation check + explainer */}
        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div
            className="inline-flex items-center gap-2 text-xs text-[color:var(--text-2)]"
            data-testid="automation-engine-next-check"
          >
            <Calendar className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
            <span className="uppercase tracking-wider text-[10px] text-[color:var(--text-3)]">Queue check</span>
            <span className="font-medium text-[color:var(--text-1)]">
              {_fmtDateTime(status?.next_automation_check)}
            </span>
          </div>
          <div className="min-w-[240px] flex-1 md:max-w-[420px]">
            <HowItWorks />
          </div>
        </div>

        {loading ? (
          <div
            className="mt-3 text-[11px] text-[color:var(--text-3)] italic"
            data-testid="automation-engine-loading"
          >
            Loading engine status…
          </div>
        ) : null}
      </div>
    </GlassCard>
  );
}
